<script setup>
import CatalogFilter from '@/components/catalog/CatalogFilter.vue';
import CatalogFilterPrice from '@/components/catalog/CatalogFilterPrice.vue';

const props = defineProps({
  type: { type: String, required: true },
  filters: { type: Object, required: true },
  forFilters: { type: Object, default: null },
  allFilterValues: { type: Object, default: null },
  activeFilterTags: { type: Array, default: () => [] },
  backgroundColor: { type: String, default: () => '' },
  filterShowUrl: { type: Boolean, default: false },
  getLinkFn: { type: Function, required: true },
});

const emit = defineEmits(['set-filter', 'remove-tag', 'clear-filters']);

function setFilter(field, value) {
  emit('set-filter', field, value);
}
</script>

<template>
  <div :class="[`catalog__filters catalog__filters--${backgroundColor}`]">
    <ScrollPanel style="width: 100%">
      <div class="catalog__filters-header"></div>
      <CatalogFilter
        v-if="forFilters?.brand"
        :label="$t('filters.brand')"
        :options="forFilters?.brand || []"
        :active-options="forFilters?.brand || []"
        :selected="filters.brand"
        :filter-key="'brand'"
        :filter-show-url="filterShowUrl.brand"
        :get-link-fn="getLinkFn"
        multi
        @change="(val) => setFilter('brand', val)"
      />
      <CatalogFilter
        v-if="forFilters?.model"
        :label="$t('filters.model')"
        :options="forFilters?.model || []"
        :active-options="forFilters?.model || []"
        :selected="filters.model"
        :filter-key="'model'"
        :filter-show-url="filterShowUrl.model"
        :get-link-fn="getLinkFn"
        multi
        @change="(val) => setFilter('model', val)"
      />
      <CatalogFilterPrice
        v-if="type === 'stock'"
        :min="forFilters?.price?.min ?? allFilterValues?.price?.min"
        :max="forFilters?.price?.max ?? allFilterValues?.price?.max"
        :value-from="filters.price_from"
        :value-to="filters.price_to"
        @change="
          ({ from, to }) => {
            setFilter('price_from', from);
            setFilter('price_to', to);
          }
        "
      />
      <CatalogFilter
        v-if="forFilters?.color"
        :label="$t('filters.color')"
        :options="forFilters?.color || []"
        :active-options="forFilters?.color || []"
        :selected="filters.color"
        :filter-key="'color'"
        :filter-show-url="filterShowUrl.color"
        :get-link-fn="getLinkFn"
        multi
        @change="(val) => setFilter('color', val)"
      />
      <CatalogFilter
        v-if="forFilters?.bodytype"
        :label="$t('filters.bodytype')"
        :options="forFilters?.bodytype || []"
        :active-options="forFilters?.bodytype || []"
        :selected="filters.bodytype"
        :filter-key="'bodytype'"
        :filter-show-url="filterShowUrl.bodytype"
        :get-link-fn="getLinkFn"
        multi
        @change="(val) => setFilter('bodytype', val)"
      />
      <CatalogFilter
        v-if="forFilters?.transmission"
        :label="$t('filters.transmission')"
        :options="forFilters?.transmission || []"
        :active-options="forFilters?.transmission || []"
        :selected="filters.transmission"
        :filter-key="'transmission'"
        :filter-show-url="filterShowUrl.transmission"
        :get-link-fn="getLinkFn"
        multi
        @change="(val) => setFilter('transmission', val)"
      />
      <CatalogFilter
        v-if="forFilters?.wheel_drive"
        :label="$t('filters.wheel_drive')"
        :options="forFilters?.wheel_drive || []"
        :active-options="forFilters?.wheel_drive || []"
        :selected="filters.wheel_drive"
        :filter-key="'wheel_drive'"
        :filter-show-url="filterShowUrl.wheel_drive"
        :get-link-fn="getLinkFn"
        multi
        @change="(val) => setFilter('wheel_drive', val)"
      />
      <CatalogFilter
        v-if="forFilters?.fuel"
        :label="$t('filters.fuel')"
        :options="forFilters?.fuel || []"
        :active-options="forFilters?.fuel || []"
        :selected="filters.fuel"
        :filter-key="'fuel'"
        :filter-show-url="filterShowUrl.fuel"
        :get-link-fn="getLinkFn"
        multi
        @change="(val) => setFilter('fuel', val)"
      />
      <CatalogFilter
        v-if="forFilters?.region"
        :label="$t('filters.region')"
        :options="forFilters?.region || []"
        :active-options="forFilters?.region || []"
        :selected="filters.region"
        :filter-key="'region'"
        :filter-show-url="filterShowUrl.region"
        :get-link-fn="getLinkFn"
        multi
        @change="(val) => setFilter('region', val)"
      />
      <CatalogFilter
        v-if="forFilters?.dealer"
        :label="$t('filters.dealer')"
        :options="forFilters?.dealer || []"
        :active-options="forFilters?.dealer || []"
        :selected="filters.dealer"
        :filter-key="'dealer'"
        :filter-show-url="filterShowUrl.dealer"
        :get-link-fn="getLinkFn"
        multi
        @change="(val) => setFilter('dealer', val)"
      />
      <CatalogFilterPrice
        v-if="type !== 'stock'"
        :min="forFilters?.price?.min ?? allFilterValues?.price?.min"
        :max="forFilters?.price?.max ?? allFilterValues?.price?.max"
        :value-from="filters.price_from"
        :value-to="filters.price_to"
        @change="
          ({ from, to }) => {
            setFilter('price_from', from);
            setFilter('price_to', to);
          }
        "
      />
    </ScrollPanel>
  </div>
</template>

<style scoped lang="scss">
.catalog {
  &__filters {
    position: fixed;
    left: 0;
    grid-area: filters;
    top: 0;
    z-index: 3;
    display: none;
    @include media-breakpoint-up(md) {
      border-radius: 8px;
      display: block;
      margin-top: 0;
      position: relative;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }
    &:deep(
        .catalog__filters-header
          + .catalog__filter-container
          .catalog__filter-top
      ) {
      @include media-breakpoint-up(md) {
        border-radius: 8px 8px 0 0;
        border-top: unset;
        top: 0;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 8);
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }
  }
}
</style>
